import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { GraduationCap, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/context/LanguageContext";
import { LanguageSwitcher } from "@/components/common/LanguageSwitcher";
import { toast } from "sonner";

export default function Login() {
    const { login } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(email.trim(), password);
            toast.success(t("auth.login.welcome"));
            navigate("/library");
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("auth.login.invalid"));
        } finally {
            setLoading(false);
        }
    };

    const useDemo = () => {
        setEmail("demo@reading.os");
        setPassword("Demo1234");
    };

    return (
        <div className="relative flex min-h-screen items-center justify-center bg-surface-0 px-4">
            <div className="hero-wash pointer-events-none absolute inset-0" />
            <div className="absolute right-4 top-4 z-10"><LanguageSwitcher testId="language-switcher-auth" /></div>
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="relative w-full max-w-md rounded-3xl border border-border bg-surface-1 p-7 shadow-elev-1 sm:p-8"
            >
                <Link to="/" className="mb-6 inline-flex items-center gap-2">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
                        <GraduationCap className="h-5 w-5" />
                    </div>
                    <span className="font-sans-display text-base font-semibold text-t1">LearnOS</span>
                </Link>
                <h1 className="font-sans-display text-2xl font-semibold tracking-tight text-t1">{t("auth.login.title")}</h1>
                <p className="mt-1.5 text-sm text-t3">{t("auth.login.subtitle")}</p>

                <form onSubmit={submit} className="mt-6 space-y-4">
                    <div className="space-y-1.5">
                        <Label htmlFor="email" className="text-t2">{t("auth.login.email")}</Label>
                        <Input data-testid="login-email-input" id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("auth.login.emailPlaceholder")} className="bg-surface-2" />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="password" className="text-t2">{t("auth.login.password")}</Label>
                        <Input data-testid="login-password-input" id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("auth.login.passwordPlaceholder")} className="bg-surface-2" />
                    </div>
                    <Button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full gap-2">
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{t("auth.login.submit")} <ArrowRight className="h-4 w-4" /></>}
                    </Button>
                </form>

                <button data-testid="login-demo-button" onClick={useDemo} className="mt-4 w-full rounded-lg border border-dashed border-border bg-surface-2/50 px-3 py-2 text-xs text-t3 transition-colors hover:bg-surface-2">
                    {t("auth.login.demo")} · demo@reading.os / Demo1234
                </button>

                <p className="mt-6 text-center text-sm text-t3">
                    {t("auth.login.noAccount")}{" "}
                    <Link to="/register" className="font-medium text-[hsl(var(--primary))] hover:underline">{t("auth.login.signUp")}</Link>
                </p>
            </motion.div>
        </div>
    );
}
